import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import {
  ArrowLeft, Loader2, AlertTriangle, Clock, CheckCircle2, Hourglass,
  Tag, ChevronRight, Building2, MapPin
} from 'lucide-react';
import { api } from '../contexts/AuthContext';

const statusStyle = (s) => {
  if (s === 'chiuso' || s === 'completato') return { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: CheckCircle2 };
  if (s === 'in_lavorazione') return { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', icon: Hourglass };
  return { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: Clock };
};

const priorityBadge = (p) => {
  const m = { urgente: { bg: '#dc2626', t: 'URGENTE' }, alta: { bg: '#ea580c', t: 'ALTA' }, media: { bg: '#0891b2', t: 'MEDIA' }, bassa: { bg: '#64748b', t: 'BASSA' } };
  const v = m[p] || m.media;
  return <span className="px-1.5 py-0.5 rounded text-[10px] font-bold text-white" style={{ background: v.bg }}>{v.t}</span>;
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' }); } catch (_) { return '—'; }
};

const AssetGenealogyPage = () => {
  const { hotelId, area } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [months, setMonths] = useState(24);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data: d } = await api.get(`/analytics/asset-genealogy?hotel_id=${hotelId}&area=${encodeURIComponent(area)}&months=${months}`);
        setData(d);
      } catch (e) {
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [hotelId, area, months]);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (!data) return <div className="text-center py-20 text-slate-500">Dati non disponibili</div>;

  const rec = data.recommendation;

  return (
    <div className="space-y-6" data-testid="asset-genealogy-page">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate('/intel')} data-testid="genealogy-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Indietro
        </Button>
      </div>

      <div>
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500">
          <Building2 className="w-3.5 h-3.5" /> {data.hotel_name}
          <ChevronRight className="w-3 h-3" />
          <MapPin className="w-3.5 h-3.5" /> Area / Camera
        </div>
        <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mt-1" data-testid="genealogy-area-title">{data.area}</h1>
        <p className="text-sm text-slate-500 mt-1">Storico interventi degli ultimi {data.months_window} mesi</p>
      </div>

      {rec && (
        <Card className="border-2" style={{ borderColor: rec.severity === 'high' ? '#dc2626' : '#f59e0b', background: rec.severity === 'high' ? 'rgba(220, 38, 38, 0.05)' : 'rgba(245, 158, 11, 0.05)' }} data-testid="genealogy-recommendation">
          <CardContent className="pt-4 flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 mt-0.5 shrink-0" style={{ color: rec.severity === 'high' ? '#dc2626' : '#f59e0b' }} />
            <div className="min-w-0">
              <h3 className="font-bold text-slate-900">{rec.title}</h3>
              <p className="text-sm text-slate-600 mt-1">{rec.message}</p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card data-testid="stat-total"><CardContent className="pt-4"><div className="text-[10px] uppercase font-bold text-slate-500">Totale interventi</div><div className="text-3xl font-extrabold text-slate-900">{data.total_tickets}</div></CardContent></Card>
        <Card><CardContent className="pt-4"><div className="text-[10px] uppercase font-bold text-slate-500">Aperti</div><div className="text-3xl font-extrabold text-amber-600">{data.open_count}</div></CardContent></Card>
        <Card><CardContent className="pt-4"><div className="text-[10px] uppercase font-bold text-slate-500">Chiusi</div><div className="text-3xl font-extrabold text-emerald-600">{data.closed_count}</div></CardContent></Card>
        <Card><CardContent className="pt-4"><div className="text-[10px] uppercase font-bold text-slate-500">Tempo medio</div><div className="text-xl font-extrabold text-slate-900">{data.avg_resolution_human}</div></CardContent></Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base font-bold flex items-center gap-2"><Tag className="w-4 h-4 text-blue-600" /> Per Categoria</CardTitle></CardHeader>
          <CardContent>
            {data.by_category.length === 0 ? (
              <p className="text-sm text-slate-500">Nessuna categoria</p>
            ) : (
              <div className="space-y-2" data-testid="genealogy-by-category">
                {data.by_category.map((c, i) => {
                  const max = data.by_category[0]?.count || 1;
                  const pct = (c.count / max) * 100;
                  return (
                    <div key={i} className="space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-semibold text-slate-700 truncate">{c.category}</span>
                        <span className="font-mono text-slate-500 shrink-0 ml-2">{c.count} <span className="text-slate-400">({c.closed} chiusi · {c.avg_resolution_human})</span></span>
                      </div>
                      <div className="bg-slate-100 rounded-full h-2 overflow-hidden">
                        <div className="h-full bg-blue-500" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base font-bold flex items-center gap-2"><Clock className="w-4 h-4 text-amber-600" /> Trend Mensile</CardTitle></CardHeader>
          <CardContent>
            {data.by_month.length === 0 ? (
              <p className="text-sm text-slate-500">Nessun dato mensile</p>
            ) : (
              <div className="flex items-end gap-1 h-32" data-testid="genealogy-trend">
                {data.by_month.map((m, i) => {
                  const max = Math.max(...data.by_month.map(x => x.count), 1);
                  const h = (m.count / max) * 100;
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                      <div className="text-[9px] font-mono text-slate-500">{m.count}</div>
                      <div className="w-full bg-blue-500 rounded-t" style={{ height: `${h}%`, minHeight: '2px' }} title={`${m.month}: ${m.count}`} />
                      <div className="text-[8px] font-mono text-slate-400 truncate w-full text-center">{m.month.slice(-5)}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base font-bold">Timeline completa ({data.timeline.length})</CardTitle></CardHeader>
        <CardContent>
          {data.timeline.length === 0 ? (
            <p className="text-sm text-slate-500">Nessun ticket nel periodo</p>
          ) : (
            <div className="space-y-2" data-testid="genealogy-timeline">
              {data.timeline.map((t) => {
                const st = statusStyle(t.status);
                const Icon = st.icon;
                return (
                  <Link
                    to={`/tickets/${t.id}`}
                    key={t.id}
                    className={`flex items-start gap-3 p-3 rounded-lg border ${st.border} ${st.bg} hover:shadow-sm transition`}
                    data-testid={`genealogy-timeline-${t.id}`}
                  >
                    <Icon className={`w-4 h-4 ${st.text} shrink-0 mt-0.5`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-900 truncate">{t.title}</span>
                        {priorityBadge(t.priority)}
                        <Badge variant="secondary" className="text-[10px]">{t.category_name}</Badge>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-500 flex-wrap">
                        <span>Aperto: <b className="text-slate-700">{formatDate(t.created_at)}</b></span>
                        {t.closed_at && <span>Chiuso: <b className="text-slate-700">{formatDate(t.closed_at)}</b></span>}
                        {t.assigned_to_name && <span>Tecnico: <b className="text-slate-700">{t.assigned_to_name}</b></span>}
                      </div>
                    </div>
                    <Badge variant="outline" className={`text-[10px] ${st.text} ${st.border} shrink-0`}>{t.status}</Badge>
                  </Link>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AssetGenealogyPage;
